import React, { useEffect, useMemo, useState } from 'react';
import { Column } from '@shared/ui/atoms/StructuralLayout';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import moment, { Moment } from "moment";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { MountedContentCard } from '@shared/ui/atoms/MountedContentCard';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { ButtonSize, NewButtonProps } from '@shared/ui/atoms/buttons/ButtonStyling';
import IconButton, { IconButtonProps } from '@shared/ui/molecules/icon-button/IconButton';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { useParams } from "react-router";
import {
  bookHoliday,
  deleteHolidayBooking,
  loadUsersHolidays,
  selectHolidayUserInfo,
  selectIsLoading,
  selectUsersCustomLeave,
  selectUsersHolidayGroupings
} from '@features/holidays/store/holidays-v2.duck';
import Layout from '@shared/ui/organisms/layout/Layout';
import { DATE_FORMAT, isDateInRange, PRETTY_SHORT_DATE_FORMAT } from '@shared/utils/DateUtils';
import { groupCustomLeaveDays } from "./models/Holidays.model";
import { HolidayDateRangePicker } from "./components/HolidayDateRangePicker";
import { HolidayBreakdownSummary } from "./components/HolidayBreakdownSummary";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Period } from '@features/whereabouts/models/movements.models';
import { useTranslation } from "react-i18next";
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { UserInfoAndLeaveTypeBox } from '@shared/ui/molecules/UserInfoAndLeaveTypeBox';
import { selectConfig, selectHasGlobalConfigLoaded } from '@shared/store/config.duck';
import { failureNotification } from '@shared/store/notification.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { HOLIDAY_ID, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';

export function HolidayCreateEditPage() {
  const dispatch = useDispatch();
  const params = useParams<any>();
  const [fromOriginalDate, setFromOriginalDate] = useState<Moment | undefined>(undefined);
  const [toOriginalDate, setToOriginalDate] = useState<Moment | undefined>(undefined);
  const [fromDate, setFromDate] = useState<Moment | undefined>(undefined);
  const [toDate, setToDate] = useState<Moment | undefined>(undefined);
  const [startDatePeriod, setStartDatePeriod] = useState(Period.AllDay);
  const [endDatePeriod, setEndDatePeriod] = useState(Period.AllDay);
  const [leaveType, setLeaveType] = useState(HOLIDAY_ID);
  const {t} = useTranslation();
  const bookedHolidays = useSelector(selectUsersHolidayGroupings);
  const customLeave = useSelector(selectUsersCustomLeave);
  const sortedBookedHolidays = useMemo(() => [...bookedHolidays].sort((a, b) => moment(a.fromDate) < moment(b.fromDate) ? -1 : 1), [bookedHolidays]);
  const isLoading = useSelector(selectIsLoading);
  const holidayUserInfo = useSelector(selectHolidayUserInfo);
  const config = useSelector(selectConfig);
  const isConfigLoading = useSelector(selectHasGlobalConfigLoaded);
  const currentUser = useSelector(selectCurrentUser);
  const currentUserId = currentUser?.id;
  const userIdFromUrl = params?.user ?? currentUserId;

  const dateParam: Moment | undefined = useMemo(() => params.date ? moment(params.date) : undefined, [params])

  useEffect(() => {
    if (userIdFromUrl && isConfigLoading) {
      dispatch(loadUsersHolidays({holidayYear: dateParam?.clone(), userId: userIdFromUrl}));
    }
  }, [userIdFromUrl, dateParam, isConfigLoading, dispatch]);

  const isEditing = useMemo(() => {
    if (!dateParam) {
      return false;
    }

    // Holiday bookings
    const holidayToEdit = sortedBookedHolidays.find(bh => isDateInRange(dateParam, moment(bh.fromDate), moment(bh.toDate)));
    if (holidayToEdit) {
      const from = moment(holidayToEdit.fromDate);
      const to = moment(holidayToEdit.toDate);
      setFromDate(from);
      setFromOriginalDate(from);
      setToDate(to);
      setToOriginalDate(to);
      if (holidayToEdit.holidayBookingList.length > 0) {
        const first = holidayToEdit.holidayBookingList[0];
        const last = holidayToEdit.holidayBookingList[holidayToEdit.holidayBookingList.length - 1];
        setStartDatePeriod(first.am && first.pm ? Period.AllDay : (first.am ? Period.AM : Period.PM));
        setEndDatePeriod(last.am && last.pm ? Period.AllDay : (last.am ? Period.AM : Period.PM));
      }
      setLeaveType(HOLIDAY_ID);
      return true;
    }

    // Custom leave-type bookings
    const customLeaveToEdit = groupCustomLeaveDays(customLeave).find(g => isDateInRange(dateParam, moment(g.fromDate), moment(g.toDate)));
    console.log(customLeaveToEdit);
    if (customLeaveToEdit) {
      const from = moment(customLeaveToEdit.fromDate);
      const to = moment(customLeaveToEdit.toDate);
      console.log(from, to);
      setFromDate(from.clone());
      setFromOriginalDate(from.clone());
      setToDate(to.clone());
      setToOriginalDate(to.clone());
      const daysInRange = customLeave
        .filter(d => isDateInRange(moment(d.date), from, to))
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      if (daysInRange.length > 0) {
        const first = daysInRange[0];
        const last = daysInRange[daysInRange.length - 1];
        setStartDatePeriod(first.am && first.pm ? Period.AllDay : (first.am ? Period.AM : Period.PM));
        setEndDatePeriod(last.am && last.pm ? Period.AllDay : (last.am ? Period.AM : Period.PM));
      }
      setLeaveType(customLeaveToEdit.whereaboutsId);
      return true;
    }

    return false;
  }, [dateParam, sortedBookedHolidays, customLeave]);



  const onDatesSelected = (startDatePeriod: Period, endDatePeriod: Period, startDate?: Moment, endDate?: Moment) => {
    setStartDatePeriod(startDatePeriod);
    setEndDatePeriod(endDatePeriod);

    if (startDate) setFromDate(moment(startDate));
    else setFromDate(undefined);

    if (endDate) setToDate(moment(endDate));
    else setToDate(undefined);
  }

  const onBookHolidayClick = () => {
    if (!holidayUserInfo.userId) {
      dispatch(failureNotification('No user found to book holiday for.'));
      return;
    }
    dispatch(bookHoliday({
      fromDate: fromDate,
      fromDatePeriod: startDatePeriod,
      toDate: toDate,
      toDatePeriod: endDatePeriod,
      userId: holidayUserInfo.userId,
      originalFromDate: fromOriginalDate,
      originalToDate: toOriginalDate,
      leaveType: leaveType,
    }));
  }

  const deleteHolidayClicked = () => {
    if (!holidayUserInfo.userId) {
      dispatch(failureNotification('No user found to book holiday for.'));
      return;
    }
    dispatch(deleteHolidayBooking({
      fromDate: fromDate,
      fromDatePeriod: startDatePeriod,
      toDate: toDate,
      toDatePeriod: endDatePeriod,
      userId: holidayUserInfo.userId
    }));
  }

  const onLeaveTypeSelected = (wo: WhereaboutsOption) => {
    setLeaveType(wo.id);
  }
  console.log(fromDate?.format(DATE_FORMAT), toDate?.format(DATE_FORMAT));

  return (
    <Layout>
      {(isLoading) ? <LoadingSpinner fullScreen={true} /> :
        <MountedContentCardWrapper centre={true}>
          <GoBackButton/>
          <Column style={{position: 'relative', marginBottom: 8}}>
            <HeadlineLarge style={{marginBottom: 12, marginLeft: 'auto', marginRight: 'auto'}}>{t(isEditing ? 'holidays.edit-holiday-title' : 'holidays.add-holiday-title')}</HeadlineLarge>
            <BodyRegular style={{marginBottom: 24, marginLeft: 'auto', marginRight: 'auto'}}>{t('holidays.holiday-year-span', {from: config.getHolidayYearStartDate(dateParam?.clone())?.format(PRETTY_SHORT_DATE_FORMAT), to: config.getHolidayYearEndDate(dateParam?.clone())?.format(PRETTY_SHORT_DATE_FORMAT)})}</BodyRegular>

            <UserInfoAndLeaveTypeBox firstName={holidayUserInfo.firstName}
                                     lastName={holidayUserInfo.lastName}
                                     teamName={holidayUserInfo.teamName}
                                     onLeaveTypeSelected={onLeaveTypeSelected}
                                     selectedLeaveType={leaveType}
                                     userId={holidayUserInfo.userId} />

            <HolidayDateRangePicker fromDate={fromDate}
                                    toDate={toDate}
                                    disabled={isEditing}
                                    fromDatePeriod={startDatePeriod}
                                    toDatePeriod={endDatePeriod}
                                    onDatesSelected={onDatesSelected} />
          </Column>

          {fromDate && <HolidayBreakdownSummary fromDate={fromDate} toDate={toDate} userId={params.user}
                                                startDatePeriod={startDatePeriod} endDatePeriod={endDatePeriod}
                                                selectedWhereaboutsTypeId={leaveType}
                                                style={{marginBottom: 16}} />}


          <ButtonWithMargin text={isEditing ? 'button.save-changes' : 'button.book-holiday'}
                            fullWidth={true}
                            disabled={isEditing}
                            dataTest={"book-holiday-button"}
                            size={ButtonSize.Large}
                            click={() => onBookHolidayClick()}/>

          {isEditing && <IconButtonWithMargin icon={IconTypes.Bin} type="outline-borderless"
                                              text="button.delete-holiday-request" fullWidth={true}
                                              size="mediumlarge" onClick={deleteHolidayClicked}/>}

        </MountedContentCardWrapper>
      }
    </Layout>
  )
}

const IconButtonWithMargin = styled(IconButton)<IconButtonProps>`
  margin-bottom: 16px;
`

const ButtonWithMargin = styled(PrimaryButton)<NewButtonProps>`
  margin: 0 auto 24px auto;
  @media (${tablet}) {
    width: 343px;
  }
`

const MountedContentCardWrapper = styled(MountedContentCard)`
  margin-top: 40px;
  max-width: 680px;
  margin-bottom: 50px;
`

